import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import type {BateriaForma, BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {tope} from '@/lib/campos-texto'

export function PersonasToolbar({
  search,
  onSearchChange,
  estadoFiltroSlot,
  filtroForma,
  onFiltroFormaChange,
  selected,
  matchingCount,
  allMatchingSelected,
  onSelectAllMatching,
  onDeselectAll,
  readOnly,
  selectedPaquetes,
  imprimirPending,
  onImprimirSeleccion,
  validarPending,
  onValidarSeleccion,
  quitarValidacionPending,
  onQuitarValidacionSeleccion,
  onRetirarSeleccion,
  onRevisarSeleccionadas,
  onAgregarPaquetes,
  onAgregarPersona,
}: {
  search: string
  onSearchChange: (next: string) => void
  estadoFiltroSlot: ReactNode
  filtroForma: BateriaForma | ''
  onFiltroFormaChange: (next: BateriaForma | '') => void
  selected: Set<string>
  matchingCount: number
  allMatchingSelected: boolean
  onSelectAllMatching: () => void
  onDeselectAll: () => void
  readOnly: boolean
  selectedPaquetes: BateriaPaquete[]
  imprimirPending: boolean
  onImprimirSeleccion: () => void
  validarPending: boolean
  onValidarSeleccion: () => void
  quitarValidacionPending: boolean
  onQuitarValidacionSeleccion: (paquetes: BateriaPaquete[]) => void
  onRetirarSeleccion: (paquetes: BateriaPaquete[]) => void
  onRevisarSeleccionadas: () => void
  onAgregarPaquetes: () => void
  onAgregarPersona: () => void
}) {
  const {t} = useTranslation()
  // Solo se anticipa la duda pendiente; los demás bloqueos los reporta el servidor. Con selección mixta
  // el botón sigue vivo: el endpoint valida las buenas y devuelve las bloqueadas con su motivo.
  const conDudas = selectedPaquetes.filter((p) => p.pendientes_de_decision > 0).length
  const nadaQueValidar = selectedPaquetes.length > 0 && conDudas === selectedPaquetes.length
  const validarAyuda = nadaQueValidar
    ? t('admin.bateria.detail.ayuda.validarTodasConDudas')
    : t('admin.bateria.detail.ayuda.validarAlgunasConDudas', {count: conDudas})
  // Se habilita con al menos un validado; el servidor omite el resto con su motivo.
  const validadosEnSeleccion = selectedPaquetes.filter((p) => p.estado === 'validado').length
  return (
    <>
      <Input
        maxLength={tope('BUSCA')}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('admin.bateria.detail.searchPlaceholder')}
        className="max-w-xs"
        data-testid="paquetes-buscar"
      />
      {estadoFiltroSlot}
      <select
        value={filtroForma}
        onChange={(e) => onFiltroFormaChange(e.target.value as BateriaForma | '')}
        aria-label={t('admin.bateria.detail.filtroForma')}
        data-testid="paquetes-filtro-forma"
        className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
        <option value="">{t('admin.bateria.detail.filtroFormaTodas')}</option>
        <option value="A">{t('admin.bateria.formaValue', {forma: 'A'})}</option>
        <option value="B">{t('admin.bateria.formaValue', {forma: 'B'})}</option>
      </select>
      {/* «Seleccionar los N» across the WHOLE filtered set (not just the visible page). */}
      <span className="ml-auto flex flex-wrap items-center gap-2">
        {!readOnly && (
          <>
            <Button
              type="button"
              size="xs"
              variant="outline"
              data-testid="agregar-paquetes"
              onClick={onAgregarPaquetes}>
              {t('admin.bateria.detail.agregar')}
            </Button>
            {/* Alta por cédula: la única puerta cuando la persona no está en la nómina (ad-hoc). */}
            <Button type="button" size="xs" variant="outline" data-testid="agregar-persona" onClick={onAgregarPersona}>
              {t('admin.bateria.detail.agregarPersona')}
            </Button>
          </>
        )}
        {selected.size > 0 && (
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="paquetes-seleccionados-count">
            {t('admin.bateria.detail.seleccionadosCount', {count: selected.size})}
          </span>
        )}
        <Button
          type="button"
          size="xs"
          variant="outline"
          data-testid="paquetes-seleccionar-todos"
          disabled={matchingCount === 0}
          onClick={allMatchingSelected ? onDeselectAll : onSelectAllMatching}>
          {allMatchingSelected
            ? t('admin.bateria.detail.quitarSeleccion')
            : t('admin.bateria.detail.seleccionarTodos', {count: matchingCount})}
        </Button>
        {selected.size > 0 && (
          <>
            <Button
              type="button"
              size="xs"
              variant="outline"
              data-testid="imprimir-seleccionados"
              disabled={imprimirPending}
              onClick={onImprimirSeleccion}>
              {t('admin.bateria.detail.imprimirSeleccionados')}
            </Button>
            {/* Revisar antes que validar: es el orden del trabajo real. */}
            <Button
              type="button"
              size="xs"
              variant="outline"
              data-testid="revisar-seleccionadas"
              onClick={onRevisarSeleccionadas}>
              {t('admin.bateria.detail.revisarSeleccionadas')}
            </Button>
            {!readOnly && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                data-testid="validar-seleccionados"
                disabled={validarPending || nadaQueValidar}
                onClick={onValidarSeleccion}>
                {t('admin.bateria.detail.validarSeleccionados')}
              </Button>
            )}
            {conDudas > 0 && !readOnly && (
              <span className="font-mono text-[11px] text-muted-foreground" data-testid="validar-seleccion-dudas">
                {validarAyuda}
              </span>
            )}
            {!readOnly && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                data-testid="quitar-validacion-seleccionados"
                disabled={quitarValidacionPending || validadosEnSeleccion === 0}
                onClick={() => onQuitarValidacionSeleccion(selectedPaquetes)}>
                {t('admin.bateria.detail.quitarValidacionSeleccionados')}
              </Button>
            )}
            {!readOnly && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                data-testid="retirar-seleccionados"
                onClick={() => onRetirarSeleccion(selectedPaquetes)}>
                {t('admin.bateria.detail.retirarSeleccionados')}
              </Button>
            )}
          </>
        )}
      </span>
    </>
  )
}
